/**
 * Conversation domain wire contract — REST paths, request/response
 * schemas, socket event names, and socket payload schemas in one file.
 * Backend validates against these; web and mobile RTK Query slices and
 * socket listeners import the same objects, so a rename fails compiles
 * everywhere instead of drifting per app.
 *
 * Shape sources (verified against the Prisma selects, 2026-07-21):
 * - conversationService.getConversations / getMessages / sendMessage
 * - conversationController markRead / search / link-preview responses
 *
 * Wire facts that bite if forgotten:
 * - Pagination is CURSOR-based ({ nextCursor: string | null }), not the
 *   page/pageSize envelope other domains use.
 * - `sender` on a message is NULLABLE: support/admin messages have
 *   senderId = null + adminSenderId set. Consumers must render degraded,
 *   never assume a sender.
 * - createConversation returns participants WITHOUT nested user objects;
 *   the list endpoint returns participants WITH them. Two schemas, on
 *   purpose — do not "fix" one to match the other.
 * - Search rows are camelCase on the wire (the raw SQL SELECT aliases
 *   snake_case columns); `headline` carries ts_headline <b> markers
 *   around the raw message, which is never HTML (see SearchResultRowSchema).
 * - Link preview returns null data on any fetch failure (200, not 500).
 */
import { z } from 'zod';
import { IsoDateTimeString, envelope } from './_primitives';

// ─── Routes (paths relative to the API root) ───────────────────────────

export const CONVERSATION_ROUTES = {
  list: 'conversations',
  create: 'conversations',
  search: 'conversations/search',
  linkPreview: 'conversations/link-preview',
  messages: (conversationId: string) =>
    `conversations/${conversationId}/messages`,
  send: (conversationId: string) =>
    `conversations/${conversationId}/messages`,
  markRead: (conversationId: string) =>
    `conversations/${conversationId}/read`,
  addReaction: (messageId: string) =>
    `conversations/messages/${messageId}/reactions`,
  removeReaction: (messageId: string, emoji: string) =>
    `conversations/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
} as const;

// ─── Socket contract ────────────────────────────────────────────────────
// Single source for the event names that used to be triplicated across
// backend constants/socketEvents.js, web services/constants.js, and
// mobile utils/constants.ts. MESSAGE_RECEIVED carries a MessageSchema
// payload; the other emitted payloads have dedicated schemas below.
// JOIN/LEAVE/TYPING are client→server room-control events (no payload
// schema — they carry only a conversationId string).

export const CONVERSATION_SOCKET_EVENTS = {
  JOIN_CONVERSATION: 'conversation:join',
  LEAVE_CONVERSATION: 'conversation:leave',
  MESSAGE_RECEIVED: 'message:received',
  MESSAGE_EDITED: 'message:edited',
  MESSAGE_DELETED: 'message:deleted',
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',
  CONVERSATION_READ: 'conversation:read',
  UNREAD_COUNT: 'unread:count',
  REACTION_ADDED: 'reaction:added',
  REACTION_REMOVED: 'reaction:removed',
} as const;

// ─── Domain constants ───────────────────────────────────────────────────

/**
 * Untrusted-user message cap (renter↔vendor + support chat). The backend
 * sanitizer ceiling in constants/messageLimits.js derives from the ADMIN
 * cap and must stay strictly greater than this — see that file's header
 * before touching either number.
 */
export const MAX_MESSAGE_LENGTH = 2000;

/** Cap on attachments per message (unbounded before 2026-07-21). */
export const MAX_MESSAGE_ATTACHMENTS = 10;

/** Matches the emoji column width (varchar 32). */
export const MAX_REACTION_EMOJI_LENGTH = 32;

// Cursor-page limits: default/max per endpoint, clamped by the query
// schemas so a client can't request an unbounded page.
export const CONVERSATION_PAGE_LIMITS = {
  list: { default: 20, max: 50 },
  messages: { default: 50, max: 100 },
  search: { default: 20, max: 50 },
} as const;

// ─── Enums ──────────────────────────────────────────────────────────────

export const ConversationTypeSchema = z.enum(['DIRECT', 'ORDER', 'SUPPORT']);
export type ConversationType = z.infer<typeof ConversationTypeSchema>;

export const MessageContentTypeSchema = z.enum(['TEXT', 'SYSTEM']);
export type MessageContentType = z.infer<typeof MessageContentTypeSchema>;

export const AttachmentTypeSchema = z.enum([
  'IMAGE',
  'VIDEO',
  'AUDIO',
  'DOCUMENT',
  'OTHER',
]);
export type AttachmentType = z.infer<typeof AttachmentTypeSchema>;

/**
 * Staging-side mime→AttachmentType mapping, shared by every compose
 * surface (web messaging, admin support replies). Chat uploads
 * only ever stage image/video/document files (the package input's
 * ALLOWED_TYPES gate), so AUDIO/OTHER are unreachable from a picker and
 * everything non-image/video lands as DOCUMENT.
 */
export const attachmentTypeFromMime = (mimeType: string): AttachmentType => {
  if (mimeType.startsWith('image/')) return 'IMAGE';
  if (mimeType.startsWith('video/')) return 'VIDEO';
  return 'DOCUMENT';
};

// ─── User shapes on the wire ────────────────────────────────────────────

/** Preview-sized sender (conversation list's last-message preview). */
export const ConversationUserLiteSchema = z
  .object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  })
  .strict();
export type ConversationUserLite = z.infer<typeof ConversationUserLiteSchema>;

/**
 * Participant user in the conversation list. `resolvedProfilePic` is
 * service-attached (vendor store pic → user pic → null).
 */
export const ConversationUserSchema = ConversationUserLiteSchema.extend({
  profilePicS3Path: z.string().nullable(),
  vendor: z
    .object({
      id: z.string(),
      storeName: z.string(),
      profilePicS3Path: z.string().nullable(),
    })
    .strict()
    .nullable(),
  resolvedProfilePic: z.string().nullable(),
});
export type ConversationUser = z.infer<typeof ConversationUserSchema>;

/** Message sender (vendor block is pic-only here). NULL for support/admin
 * messages — consumers render a degraded "GearGhost Support" row. */
export const MessageSenderSchema = ConversationUserLiteSchema.extend({
  profilePicS3Path: z.string().nullable(),
  vendor: z
    .object({ profilePicS3Path: z.string().nullable() })
    .strict()
    .nullable(),
  resolvedProfilePic: z.string().nullable(),
});
export type MessageSender = z.infer<typeof MessageSenderSchema>;

// ─── Participants ───────────────────────────────────────────────────────

export const ParticipantRowSchema = z
  .object({
    id: z.number().int(),
    conversationId: z.string(),
    userId: z.string(),
    lastReadAt: IsoDateTimeString.nullable(),
    lastReadMessageId: z.string().nullable(),
    unreadCount: z.number().int(),
    mutedUntil: IsoDateTimeString.nullable(),
    isArchived: z.boolean(),
    deletedAt: IsoDateTimeString.nullable(),
  })
  .strict();
export type ParticipantRow = z.infer<typeof ParticipantRowSchema>;

export const ParticipantWithUserSchema = ParticipantRowSchema.extend({
  user: ConversationUserSchema,
});
export type ParticipantWithUser = z.infer<typeof ParticipantWithUserSchema>;

// ─── Messages ───────────────────────────────────────────────────────────

/** Reaction row. `user` is present on getMessages (include) but absent on
 * the addReaction response (bare upsert row) — optional, not nullable. */
export const ReactionSchema = z
  .object({
    id: z.number().int(),
    messageId: z.string(),
    userId: z.string(),
    emoji: z.string(),
    createdAt: IsoDateTimeString,
    user: ConversationUserLiteSchema.optional(),
  })
  .strict();
export type Reaction = z.infer<typeof ReactionSchema>;

export const MessageAttachmentSchema = z
  .object({
    id: z.number().int(),
    messageId: z.string(),
    s3Path: z.string(),
    fileName: z.string().nullable(),
    fileType: AttachmentTypeSchema,
    mimeType: z.string().nullable(),
    fileSize: z.number().int().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    duration: z.number().int().nullable(),
    thumbnailS3Path: z.string().nullable(),
    displayOrder: z.number().int(),
  })
  .strict();
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;

const MessageCoreSchema = z
  .object({
    id: z.string(),
    conversationId: z.string(),
    senderId: z.string().nullable(),
    content: z.string(),
    contentType: MessageContentTypeSchema,
    editedAt: IsoDateTimeString.nullable(),
    deletedAt: IsoDateTimeString.nullable(),
    createdAt: IsoDateTimeString,
    updatedAt: IsoDateTimeString,
    adminSenderId: z.string().nullable(),
  })
  .strict();

/** Full message (getMessages, sendMessage, MESSAGE_RECEIVED socket). */
export const MessageSchema = MessageCoreSchema.extend({
  sender: MessageSenderSchema.nullable(),
  attachments: z.array(MessageAttachmentSchema),
  reactions: z.array(ReactionSchema),
});
export type Message = z.infer<typeof MessageSchema>;

/** Last-message preview in the conversation list (take-1 include).
 * Attachments ride along so an attachment-only newest message can
 * preview as "Sent a photo" instead of a blank row. */
export const MessagePreviewSchema = MessageCoreSchema.extend({
  sender: ConversationUserLiteSchema.nullable(),
  attachments: z.array(MessageAttachmentSchema),
});
export type MessagePreview = z.infer<typeof MessagePreviewSchema>;

/**
 * List-preview text for a conversation's newest message. Users can send
 * attachment-only messages (empty content), which would otherwise render
 * a blank preview row. Text always wins; the label derives from the
 * attachment types. ONE copy here because every list consumer (web +
 * mobile Messages tabs, admin support inbox) needs the identical rule —
 * per-app copies of exactly this kind of display rule have drifted
 * before.
 */
export const previewTextForMessage = (msg: {
  content: string;
  attachments: { fileType: string }[];
}): string => {
  if (msg.content.trim().length > 0) return msg.content;
  if (msg.attachments.length === 0) return '';
  const allImages = msg.attachments.every((a) => a.fileType === 'IMAGE');
  if (msg.attachments.length === 1) {
    return allImages ? 'Sent a photo' : 'Sent an attachment';
  }
  return `Sent ${msg.attachments.length} ${allImages ? 'photos' : 'attachments'}`;
};

// ─── Conversations ──────────────────────────────────────────────────────

const ConversationCoreSchema = z
  .object({
    id: z.string(),
    type: ConversationTypeSchema,
    subject: z.string().nullable(),
    createdAt: IsoDateTimeString,
    updatedAt: IsoDateTimeString,
  })
  .strict();

export const ConversationListItemSchema = ConversationCoreSchema.extend({
  participants: z.array(ParticipantWithUserSchema),
  /** 0 or 1 items — the newest non-deleted message. */
  messages: z.array(MessagePreviewSchema).max(1),
});
export type ConversationListItem = z.infer<typeof ConversationListItemSchema>;

/** createConversation response — participants carry NO user objects. */
export const CreatedConversationSchema = ConversationCoreSchema.extend({
  participants: z.array(ParticipantRowSchema),
});
export type CreatedConversation = z.infer<typeof CreatedConversationSchema>;

// ─── Page payloads ──────────────────────────────────────────────────────

export const ConversationsPageSchema = z
  .object({
    conversations: z.array(ConversationListItemSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ConversationsPage = z.infer<typeof ConversationsPageSchema>;

export const MessagesPageSchema = z
  .object({
    messages: z.array(MessageSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type MessagesPage = z.infer<typeof MessagesPageSchema>;

// ─── Loaded pages (a thread's scroll-back) ──────────────────────────────
//
// A thread loads 50 messages at a time, newest first, and the viewer
// scrolls up for older pages. Every app (web and admin through
// @gearghost/messaging-ui, mobile's own screens) holds the loaded pages
// as an RTK Query infinite query: `{ pages }`, the newest page first and
// each page newest first. The helpers below are the ONE copy of how that
// cache is read and written, for the user-side thread (MessagesPage) and
// the admin inbox's (AdminSupportMessagesPage) alike. Before them every
// surface but mobile's thread read a single page, and nothing older than
// the newest 50 messages could be reached (2026-09-25).

/** A thread's loaded pages as the infinite query cache holds them. */
export interface LoadedMessagePages<M extends { id: string }> {
  pages: Array<{ messages: M[] }>;
}

/**
 * `infiniteQueryOptions` for every paged messages endpoint. The first
 * page goes without a cursor; each next (older) page asks for the one
 * the last loaded page carried. `null` there means the oldest message is
 * loaded, which RTK reads as "no next page". On a refetch (the reconnect
 * catch-up, a tag invalidation) RTK re-reads every loaded page starting
 * from the newest, so a re-read never replays an old page's cursor.
 */
export const messagePagesQueryOptions = {
  initialPageParam: null as string | null,
  getNextPageParam: (lastPage: { nextCursor: string | null }) => lastPage.nextCursor,
};

/** Every loaded message as one thread, newest first. */
export const loadedMessages = <M extends { id: string }>(
  loaded: LoadedMessagePages<M>,
): M[] => loaded.pages.flatMap((page) => page.messages);

/** The loaded message with this id, on whichever page holds it. */
export const findLoadedMessage = <M extends { id: string }>(
  loaded: LoadedMessagePages<M>,
  messageId: string,
): M | undefined => {
  for (const page of loaded.pages) {
    const found = page.messages.find((m) => m.id === messageId);
    if (found) return found;
  }
  return undefined;
};

/**
 * Add a message that just arrived (socket event, send response) to the
 * front of the newest page. Your own message arrives twice, once from
 * each, so a message any loaded page already holds is left alone.
 */
export const addArrivedMessage = <M extends { id: string }>(
  loaded: LoadedMessagePages<M>,
  message: M,
): void => {
  if (findLoadedMessage(loaded, message.id)) return;
  const newestPage = loaded.pages[0];
  if (!newestPage) {
    // RTK stores an infinite query's data only with its first page in
    // it, so a cache without one is corrupt, not empty.
    throw new Error(`Thread cache for message ${message.id} holds no page`);
  }
  newestPage.messages.unshift(message);
};

/** Full-text search hit. camelCase per the SQL aliases; `headline` is
 * ts_headline output with <b>…</b> markers around matches. Between the
 * markers is the sender's message verbatim: ts_headline strips no tags,
 * so a consumer renders the markers alone as markup and everything else
 * as text (messaging-ui's SearchExcerpt), never the whole as HTML. */
export const SearchResultRowSchema = z
  .object({
    id: z.string(),
    content: z.string(),
    conversationId: z.string(),
    senderId: z.string().nullable(),
    createdAt: IsoDateTimeString,
    headline: z.string(),
  })
  .strict();
export type SearchResultRow = z.infer<typeof SearchResultRowSchema>;

export const SearchPageSchema = z
  .object({
    messages: z.array(SearchResultRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type SearchPage = z.infer<typeof SearchPageSchema>;

export const MarkReadResultSchema = z
  .object({
    /** Derived: SUM(unreadCount) over active, non-archived participants. */
    totalUnreadMessages: z.number().int(),
  })
  .strict();
export type MarkReadResult = z.infer<typeof MarkReadResultSchema>;

export const LinkPreviewSchema = z
  .object({
    url: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    image: z.string().nullable(),
    siteName: z.string().nullable(),
  })
  .strict();
export type LinkPreview = z.infer<typeof LinkPreviewSchema>;

// ─── Socket payloads ────────────────────────────────────────────────────
// MESSAGE_RECEIVED carries MessageSchema — no separate payload schema.
//
// Wire quirk (backend socket/emit.js): CONVERSATION-room events
// (MESSAGE_RECEIVED, REACTION_*, CONVERSATION_READ) arrive BARE — the
// event arg IS the payload below. USER-room events (UNREAD_COUNT via
// emitSocketEvent) arrive wrapped as { payload, sender }. Listeners on
// user-room events must unwrap `.payload` before parsing.

export const UnreadCountPayloadSchema = z
  .object({
    conversationId: z.string(),
    unreadCount: z.number().int(),
    totalUnreadMessages: z.number().int(),
  })
  .strict();
export type UnreadCountPayload = z.infer<typeof UnreadCountPayloadSchema>;

export const ConversationReadPayloadSchema = z
  .object({
    conversationId: z.string(),
    userId: z.string(),
    lastMessageId: z.string().nullable().optional(),
  })
  .strict();
export type ConversationReadPayload = z.infer<
  typeof ConversationReadPayloadSchema
>;

export const ReactionAddedPayloadSchema = z
  .object({
    messageId: z.string(),
    userId: z.string(),
    emoji: z.string(),
    reaction: ReactionSchema,
  })
  .strict();
export type ReactionAddedPayload = z.infer<typeof ReactionAddedPayloadSchema>;

export const ReactionRemovedPayloadSchema = z
  .object({
    messageId: z.string(),
    userId: z.string(),
    emoji: z.string(),
  })
  .strict();
export type ReactionRemovedPayload = z.infer<
  typeof ReactionRemovedPayloadSchema
>;

// ─── Request contracts ──────────────────────────────────────────────────
// Request objects are deliberately NON-strict (Zod strips unknown keys):
// during a deploy overlap an already-loaded client may still send retired
// keys (e.g. the reaction endpoints' old client-supplied conversationId)
// and must not 400 for it.

/**
 * Clients only ever create DIRECT conversations (both web call sites omit
 * `type`; mobile never creates). ORDER and SUPPORT conversations are
 * minted server-side — accepting them here would let any user fabricate
 * rows in the admin support inbox.
 */
export const CreateConversationRequestSchema = z.object({
  recipientId: z.string().min(1),
  type: z.literal('DIRECT').optional(),
  subject: z.string().max(255).optional(),
});
export type CreateConversationRequest = z.infer<
  typeof CreateConversationRequestSchema
>;

export const SendMessageAttachmentInputSchema = z.object({
  s3Path: z.string().min(1),
  fileName: z.string().nullish(),
  fileType: AttachmentTypeSchema,
  mimeType: z.string().nullish(),
  fileSize: z.number().int().nullish(),
});
export type SendMessageAttachmentInput = z.infer<
  typeof SendMessageAttachmentInputSchema
>;

/** Clients send TEXT only — SYSTEM is reserved for server-minted rows
 * (accepting it would let users spoof system messages). */
export const SendMessageRequestSchema = z
  .object({
    content: z.string().max(MAX_MESSAGE_LENGTH).optional().default(''),
    contentType: z.literal('TEXT').optional(),
    attachments: z
      .array(SendMessageAttachmentInputSchema)
      .max(MAX_MESSAGE_ATTACHMENTS)
      .optional()
      .default([]),
  })
  .refine((body) => body.content.trim().length > 0 || body.attachments.length > 0, {
    message: 'Message content or attachments required',
  });
export type SendMessageRequest = z.input<typeof SendMessageRequestSchema>;

export const MarkReadRequestSchema = z.object({
  lastMessageId: z.string().min(1).optional(),
});
export type MarkReadRequest = z.infer<typeof MarkReadRequestSchema>;

export const AddReactionRequestSchema = z.object({
  emoji: z.string().min(1).max(MAX_REACTION_EMOJI_LENGTH),
});
export type AddReactionRequest = z.infer<typeof AddReactionRequestSchema>;

const cursorField = z.string().min(1).optional();
const limitField = (limits: { default: number; max: number }) =>
  z.coerce.number().int().min(1).max(limits.max).default(limits.default);

export const ConversationsQueryRequestSchema = z.object({
  cursor: cursorField,
  limit: limitField(CONVERSATION_PAGE_LIMITS.list),
  /**
   * 'true' lists the caller's support thread among the rest, once it holds
   * a message (Tyler, 2026-10-09: support in web's Messages list, sorted by
   * its last message). Only a client that draws a support row asks, which
   * today is web alone: Tyler left it out of the app, and a client that
   * does not draw it would show the thread as a nameless row whose sends
   * never reach support.
   */
  includeSupport: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
export type ConversationsQueryRequest = z.input<
  typeof ConversationsQueryRequestSchema
>;

export const MessagesQueryRequestSchema = z.object({
  cursor: cursorField,
  limit: limitField(CONVERSATION_PAGE_LIMITS.messages),
});
export type MessagesQueryRequest = z.input<typeof MessagesQueryRequestSchema>;

export const SearchQueryRequestSchema = z.object({
  q: z.string().trim().min(1),
  cursor: cursorField,
  limit: limitField(CONVERSATION_PAGE_LIMITS.search),
});
export type SearchQueryRequest = z.input<typeof SearchQueryRequestSchema>;

export const LinkPreviewQueryRequestSchema = z.object({
  url: z.url(),
});
export type LinkPreviewQueryRequest = z.infer<
  typeof LinkPreviewQueryRequestSchema
>;

// ─── Envelopes + unwrap helpers ─────────────────────────────────────────

export const ConversationsPageEnvelopeSchema = envelope(
  ConversationsPageSchema,
);
export const CreatedConversationEnvelopeSchema = envelope(
  CreatedConversationSchema,
);
export const MessagesPageEnvelopeSchema = envelope(MessagesPageSchema);
export const MessageEnvelopeSchema = envelope(MessageSchema);
export const MarkReadEnvelopeSchema = envelope(MarkReadResultSchema);
export const SearchPageEnvelopeSchema = envelope(SearchPageSchema);
export const ReactionEnvelopeSchema = envelope(ReactionSchema);
/** Link preview fails soft: 200 with null data on any fetch error. */
/** Backend coverage note: non-hermetic (fetches arbitrary external URLs
 * via open-graph-scraper), so no backend integration pin — exercised by
 * the frontend slice drift-locks. See conversation.schema.test.js header. */
export const LinkPreviewEnvelopeSchema = envelope(LinkPreviewSchema.nullable());

export const unwrapConversationsPage = (
  r: z.infer<typeof ConversationsPageEnvelopeSchema>,
): ConversationsPage => r.data;
export const unwrapCreatedConversation = (
  r: z.infer<typeof CreatedConversationEnvelopeSchema>,
): CreatedConversation => r.data;
export const unwrapMessagesPage = (
  r: z.infer<typeof MessagesPageEnvelopeSchema>,
): MessagesPage => r.data;
export const unwrapMessage = (
  r: z.infer<typeof MessageEnvelopeSchema>,
): Message => r.data;
export const unwrapMarkReadResult = (
  r: z.infer<typeof MarkReadEnvelopeSchema>,
): MarkReadResult => r.data;
export const unwrapSearchPage = (
  r: z.infer<typeof SearchPageEnvelopeSchema>,
): SearchPage => r.data;
export const unwrapReaction = (
  r: z.infer<typeof ReactionEnvelopeSchema>,
): Reaction => r.data;
export const unwrapLinkPreview = (
  r: z.infer<typeof LinkPreviewEnvelopeSchema>,
): LinkPreview | null => r.data;
